import { useState, useEffect } from "react";
import InvitationAddonSection, {
  type InvitationAddonState,
} from "./InvitationAddonSection";
import SignatureBookAddonSection, {
  type SignatureBookAddonState,
  BLANK_SIGNATURE_BOOK_ADDON,
} from "./SignatureBookAddonSection";

// Re-export for contracts/page.tsx compatibility
export type { InvitationAddonState, SignatureBookAddonState };

export type Contract = {
  id: string;
  tenant_id: string;
  client_id: string;
  contract_number: string;
  title: string;
  event_date: string;
  event_location: string;
  package_name: string;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  status: string;
  signed_at: string | null;
  created_at: string;
  updated_at: string;
  client_name: string;
  payment_link: string | null;
  payment_status: string;
};

export interface ContractSavePayload {
  contract: Contract;
  invitationAddon: InvitationAddonState;
  signatureBookAddon: SignatureBookAddonState;
}

interface Props {
  contract: Contract | null;
  isNew: boolean;
  tenantId: string;
  clients: { id: string; full_name: string }[];
  onClose: () => void;
  onSave: (payload: ContractSavePayload) => void;
}

const blankContract: Contract = {
  id: "",
  tenant_id: "",
  client_id: "",
  contract_number: "",
  title: "",
  event_date: "",
  event_location: "",
  package_name: "",
  total_amount: 0,
  deposit_amount: 0,
  balance_amount: 0,
  status: "draft",
  signed_at: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  client_name: "",
  payment_link: null,
  payment_status: "unpaid",
};

const BLANK_ADDON: InvitationAddonState = {
  mode: "none",
  amount: 50,
  promoCode: "",
  alreadyActive: false,
};

const BLANK_SB_ADDON: SignatureBookAddonState = { ...BLANK_SIGNATURE_BOOK_ADDON };

const INPUT =
  "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors";

export default function ContractModal({
  contract,
  isNew,
  tenantId,
  clients,
  onClose,
  onSave,
}: Props) {
  const [form, setForm]                 = useState<Contract>(blankContract);
  const [invAddon, setInvAddon]         = useState<InvitationAddonState>(BLANK_ADDON);
  const [sbAddon, setSbAddon]           = useState<SignatureBookAddonState>(BLANK_SB_ADDON);
  const [copied, setCopied]             = useState(false);
  const [urlError, setUrlError]         = useState("");

  useEffect(() => {
    if (contract) {
      setForm({ ...blankContract, ...contract });
    } else {
      setForm({
        ...blankContract,
        contract_number: `CT-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
      });
    }
    setInvAddon(BLANK_ADDON);
    setSbAddon(BLANK_SB_ADDON);
  }, [contract]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = {
        ...prev,
        [name]: ["total_amount", "deposit_amount"].includes(name) ? Number(value) : value,
      };
      if (name === "total_amount" || name === "deposit_amount") {
        updated.balance_amount = updated.total_amount - updated.deposit_amount;
      }
      if (name === "client_id") {
        const found = clients.find((c) => c.id === value);
        updated.client_name = found?.full_name ?? "";
      }
      if (name === "payment_link") setUrlError("");
      return updated;
    });
  };

  const handleCopyLink = async () => {
    if (!form.payment_link) return;
    try {
      await navigator.clipboard.writeText(form.payment_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const validateUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      const u = new URL(url);
      return u.protocol === "https:" || u.protocol === "http:";
    } catch {
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.payment_link && !validateUrl(form.payment_link)) {
      setUrlError("Please enter a valid URL starting with http:// or https://");
      return;
    }
    onSave({
      contract: {
        ...form,
        payment_link: form.payment_link?.trim() || null,
        updated_at: new Date().toISOString(),
      },
      invitationAddon: invAddon,
      signatureBookAddon: sbAddon,
    });
  };

  const payStatusColors: Record<string, string> = {
    unpaid:         "text-red-400",
    deposit_paid:   "text-sky-400",
    partially_paid: "text-amber-400",
    paid:           "text-emerald-400",
    refunded:       "text-violet-400",
    cancelled:      "text-slate-400",
  };

  const showAddonSection = tenantId && form.client_id;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 shadow-lg rounded-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-[#0f172a] font-semibold text-base">
            {isNew ? "New Contract" : "Edit Contract"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-5">

          {/* ── Contract basics ── */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Contract Number</label>
              <input
                name="contract_number"
                value={form.contract_number}
                onChange={handleChange}
                required
                className={INPUT}
                placeholder="CT-2025-001"
              />
            </div>
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Client *</label>
              <select
                name="client_id"
                value={form.client_id}
                onChange={handleChange}
                required
                className={INPUT + " cursor-pointer"}
              >
                <option value="">Select client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.full_name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Title *</label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                className={INPUT}
                placeholder="Wedding Photography Contract…"
              />
            </div>
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Event Date</label>
              <input
                name="event_date"
                type="date"
                value={form.event_date}
                onChange={handleChange}
                className={INPUT}
              />
            </div>
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Package Name</label>
              <input
                name="package_name"
                value={form.package_name}
                onChange={handleChange}
                className={INPUT}
                placeholder="e.g. Elite Wedding Collection"
              />
            </div>
            <div className="col-span-2">
              <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Event Location</label>
              <input
                name="event_location"
                value={form.event_location}
                onChange={handleChange}
                className={INPUT}
                placeholder="Venue name, city, state"
              />
            </div>
          </div>

          {/* ── Financials ── */}
          <div>
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="h-px flex-1 bg-slate-100"></span>
              Financials
              <span className="h-px flex-1 bg-slate-100"></span>
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Total ($)</label>
                <input
                  name="total_amount"
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.total_amount}
                  onChange={handleChange}
                  className={INPUT}
                />
              </div>
              <div>
                <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Deposit ($)</label>
                <input
                  name="deposit_amount"
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.deposit_amount}
                  onChange={handleChange}
                  className={INPUT}
                />
              </div>
              <div>
                <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Balance (auto)</label>
                <div className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 flex items-center justify-between">
                  <span className="text-[#C8A96E] font-semibold text-sm">${Math.max(0, form.balance_amount).toLocaleString()}</span>
                  <span className="text-slate-300 text-[10px]">Total − Deposit</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Status ── */}
          <div>
            <label className="text-slate-500 text-xs mb-1.5 block uppercase tracking-wider">Contract Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className={INPUT + " cursor-pointer"}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="signed">Signed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* ── Add-ons section ── */}
          {showAddonSection && (
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="h-px flex-1 bg-slate-100"></span>
                Add-ons
                <span className="h-px flex-1 bg-slate-100"></span>
              </p>
              <InvitationAddonSection
                tenantId={tenantId}
                clientId={form.client_id}
                contractId={contract?.id}
                value={invAddon}
                onChange={setInvAddon}
              />
              <SignatureBookAddonSection
                tenantId={tenantId}
                clientId={form.client_id}
                contractId={contract?.id}
                value={sbAddon}
                onChange={setSbAddon}
              />
            </div>
          )}

          {/* ── Payment Link ── */}
          <div>
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="h-px flex-1 bg-slate-100"></span>
              Online Payment
              <span className="h-px flex-1 bg-slate-100"></span>
            </p>
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-[#C8A96E]/10 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-link-m text-[#C8A96E] text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-600 text-xs font-medium">Payment Link</p>
                  <p className="text-slate-400 text-[11px] mt-0.5">
                    Paste any external payment URL (PayPal, Zelle, bank transfer, etc.).
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-300">
                    <i className="ri-global-line text-sm"></i>
                  </span>
                  <input
                    name="payment_link"
                    type="text"
                    value={form.payment_link ?? ""}
                    onChange={handleChange}
                    placeholder="https://paypal.me/yourstudio/amount"
                    className={`w-full bg-white border rounded-lg pl-8 pr-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none transition-colors ${
                      urlError ? "border-red-400/50 focus:border-red-500" : "border-slate-200 focus:border-[#C8A96E]/50"
                    }`}
                  />
                </div>
                {form.payment_link && (
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                      copied
                        ? "bg-emerald-50 text-emerald-600"
                        : "bg-slate-100 text-slate-500 hover:text-[#0f172a] hover:bg-slate-200"
                    }`}
                  >
                    <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
                    {copied ? "Copied!" : "Copy"}
                  </button>
                )}
              </div>
              {urlError && (
                <p className="text-red-500 text-xs flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {urlError}
                </p>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-slate-500 text-xs uppercase tracking-wider flex-shrink-0">Payment Status</label>
                <select
                  name="payment_status"
                  value={form.payment_status}
                  onChange={handleChange}
                  className={`flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer ${payStatusColors[form.payment_status] ?? "text-[#0f172a]"}`}
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="deposit_paid">Deposit Paid</option>
                  <option value="partially_paid">Partially Paid</option>
                  <option value="paid">Paid in Full</option>
                  <option value="refunded">Refunded</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                {form.payment_link && form.payment_status !== "paid" && (
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg whitespace-nowrap">
                    <i className="ri-eye-line text-[#C8A96E]"></i>
                    Client sees &quot;Pay Securely&quot;
                  </div>
                )}
                {form.payment_status === "paid" && (
                  <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg whitespace-nowrap">
                    <i className="ri-checkbox-circle-line"></i>
                    Button hidden (paid)
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {isNew ? "Create Contract" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}